export interface DeviceServiceSettings {
  enable_zvt: boolean;
  base_url: string | null;
  api_key_configured: boolean;
}

export type ReceiptSeparatorStyle = "double" | "single" | "dots";
export type ReceiptTextAlignment = "left" | "center" | "right";
export type ReceiptFontSize = "small" | "normal" | "large";
export type ReceiptFontFamily = "standard" | "condensed" | "arial";

export interface ReceiptConfig {
  header_text: string | null;
  footer_text: string | null;
  logo_data: string | null;
  logo_configured: boolean;
  characters_per_line: number;
  separator_style: ReceiptSeparatorStyle;
  header_align: ReceiptTextAlignment;
  footer_align: ReceiptTextAlignment;
  title_text: string | null;
  font_size: ReceiptFontSize;
  font_family: ReceiptFontFamily;
  logo_width_percent: number;
}

export interface PickupReceiptConfig extends ReceiptConfig {
  title_text: string;
}

export interface DeviceServiceHealth {
  status: string;
  version: string | null;
  workstation_id: string | null;
}

export interface DeviceInfo {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  status: string;
}

export interface ZvtStatus {
  is_connected: boolean;
  payment_status: string;
  terminal_id: string | null;
  host: string | null;
  port: number | null;
  message: string | null;
}

export interface DeviceServiceStatus {
  connected: boolean;
  error: string | null;
  health: DeviceServiceHealth | null;
  devices: DeviceInfo[];
  zvt: ZvtStatus | null;
}

export function isZvtConfigured(settings: DeviceServiceSettings | null | undefined): boolean {
  return Boolean(settings?.enable_zvt && settings.base_url?.trim());
}

export function isDeviceServiceConfigured(settings: DeviceServiceSettings | null | undefined): boolean {
  return Boolean(settings?.base_url?.trim());
}

export function getDeviceServiceConnectionHint(
  baseUrl: string,
  error: string | null | undefined,
): string | null {
  if (!error) return null;

  const normalized = error.toLowerCase();
  const isRemoteUrl = !/localhost|127\.0\.0\.1/i.test(baseUrl);
  const isConnectFailure =
    normalized.includes("connecttimeout") ||
    normalized.includes("connection refused") ||
    normalized.includes("max retries exceeded") ||
    normalized.includes("verbindung zum campingdeviceservice fehlgeschlagen");

  if (!isConnectFailure) return null;

  if (isRemoteUrl) {
    return (
      "Wenn CampDesk auf einem anderen Server läuft, aktivieren Sie im lokalen Setup " +
      "(http://127.0.0.1:5050/setup) unter „LAN-Zugriff erlauben“ – der CampingDeviceService " +
      "startet dabei automatisch neu. Die Windows-Firewall-Regel für TCP-Port 5050 wird automatisch erstellt. " +
      "Der API-Key muss mit dem lokalen Setup übereinstimmen."
    );
  }

  return (
    "Prüfen Sie, ob der CampingDeviceService auf diesem PC läuft (Windows-Dienst) und ob die URL " +
    "http://127.0.0.1:5050 erreichbar ist."
  );
}

const STATUS_LABELS: Record<string, string> = {
  Healthy: "Gesund",
  Ready: "Bereit",
  Idle: "Bereit",
  Disabled: "Deaktiviert",
  Unavailable: "Nicht verfügbar",
  NotConfigured: "Nicht konfiguriert",
  Unknown: "Unbekannt",
};

export function formatDeviceStatus(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function deviceStatusTone(status: string): "green" | "amber" | "red" | "neutral" {
  const normalized = status.toLowerCase();
  if (["healthy", "ready", "idle"].includes(normalized)) return "green";
  if (["disabled", "notconfigured", "not configured"].includes(normalized)) return "neutral";
  if (["unavailable", "error", "failed"].includes(normalized)) return "red";
  return "amber";
}

export function statusBadgeClass(tone: "green" | "amber" | "red" | "neutral"): string {
  switch (tone) {
    case "green":
      return "bg-status-green/15 text-status-green";
    case "red":
      return "bg-status-red/15 text-status-red";
    case "amber":
      return "bg-amber-500/15 text-amber-800 dark:text-amber-200";
    default:
      return "bg-fill-tertiary text-label-secondary";
  }
}
